import { useEffect, useMemo, useState } from 'react';
import './OrganizationDetails.css';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
} from '@material-ui/core';
import { Skeleton, TableContainer } from '@mui/material';
import { config } from './config';
import TitleHeader from '../../../components/MasterComponent/TitleHeader/TitleHeader';
import { PlanBadge } from './PlanBadge';
import { useFetchData } from '../../../hooks/useFetchData';
import './TopServicesWidget.css';
import TopServicesWidget from './TopServicesWidget';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
// import {
//   TrendingUp,
//   TrendingDown,
//   DollarSign,
//   Users,
//   AlertCircle,
// } from 'lucide-react';

import {
  TrendingUp,
  TrendingDown,
  AttachMoney,
  People,
  ErrorOutline,
} from '@mui/icons-material';
import MultipleSelect from '../../../components/MasterComponent/Select/MultipleSelect';

interface AccountCost {
  billing_currency: string;
  month_number: string;
  year_number: string;
  totalcost: number;
}

interface Account {
  accountName: string;
  accountId: string;
  accountCost: AccountCost[];
}

interface Metrics {
  currentCost: number;
  previousCost: number;
  growthRate: number;
  avgCostPerAccount: number;
  highestAccount: { name: string; cost: number };
  monthTotals: { [key: string]: number };
}

const OrganizationDetails: React.FC = (props: any) => {
  const companyName = props.location.state.CompanyName;
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [allAccounts, setAllAccounts] = useState<Account[]>([]);

  const { data: allAccountsList } = useFetchData({
    fetchData: () =>
      config.getOrgCloudCost({
        companyName,
        // Don't pass accountIds - we want ALL accounts for the dropdown
      }),
    fetchOnMount: true,
    dependencies: [], // ✅ Empty - fetch only once on mount
  });

  const { data: orgCloudCostList, loader: isOrgCostListLoad } = useFetchData({
    fetchData: () =>
      config.getOrgCloudCost({
        companyName,
        accountIds:
          selectedAccountIds.length > 0 ? selectedAccountIds : undefined,
      }),
    fetchOnMount: true,
    dependencies: [selectedAccountIds],
  });

  const { data: topServiceWiseCost, loader: topServiceWiseLoad } = useFetchData(
    {
      fetchData: () =>
        config.getTopServiceWiseCost({
          companyName,
          accountIds:
            selectedAccountIds.length > 0 ? selectedAccountIds : undefined,
        }),
      fetchOnMount: true,
      dependencies: [selectedAccountIds],
    }
  );

  const { data: orgPlanDetail, loader: isStatusLoad } = useFetchData({
    fetchData: () => config.getOrgPlanDetail(companyName),
    fetchOnMount: true,
  });

  useEffect(() => {
    if (allAccountsList && allAccountsList.length > 0) {
      setAllAccounts(allAccountsList);
    }
  }, [allAccountsList]);

  const accountOptions: any[] = useMemo(() => {
    if (!allAccounts || allAccounts.length === 0) return [];

    return allAccounts.map((account: Account) => ({
      id: account.accountId || account.accountName,
      label: `${account.accountName}${
        account.accountId ? ` (${account.accountId})` : ''
      }`,
      value: account.accountId,
    }));
  }, [allAccounts]);

  const PlanBadgeSkeleton = () => {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          padding: '8px 16px',
          borderRadius: '12px',
          backgroundColor: '#f3f4f6',
        }}
      >
        <Skeleton variant="circular" width={20} height={20} />
        <Skeleton variant="text" width={120} height={24} />
        <Skeleton variant="rectangular" width={1} height={24} />
        <Skeleton variant="text" width={80} height={28} />
      </div>
    );
  };

  // Generate dynamic month headers from the data
  const generateMonthHeaders = () => {
    if (!orgCloudCostList || orgCloudCostList.length === 0) return [];

    const allMonths = new Set<string>();
    orgCloudCostList.forEach((account: Account) => {
      account.accountCost?.forEach((cost: AccountCost) => {
        const monthKey = `${cost.month_number}-${cost.year_number}`;
        allMonths.add(monthKey);
      });
    });

    // Sort months in descending order (latest first)
    const sortedMonths = Array.from(allMonths).sort((a, b) => {
      const [monthA, yearA] = a.split('-');
      const [monthB, yearB] = b.split('-');
      if (yearA !== yearB) return parseInt(yearB) - parseInt(yearA);
      return parseInt(monthB) - parseInt(monthA);
    });

    return sortedMonths;
  };

  const formatMonthHeader = (monthKey: string) => {
    const [month, year] = monthKey.split('-');
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    return `${monthNames[parseInt(month) - 1]} '${year}`;
  };

  const getCostForMonth = (accountCost: AccountCost[], monthKey: string) => {
    const [month, year] = monthKey.split('-');
    const cost = accountCost?.find(
      (c: AccountCost) => c.month_number === month && c.year_number === year
    );
    return cost ? `$${cost.totalcost.toFixed(2)}` : '-';
  };

  const monthHeaders = generateMonthHeaders();

  // Calculate metrics
  const calculateMetrics = (): Metrics | null => {
    if (!orgCloudCostList || orgCloudCostList.length === 0) return null;

    const monthTotals: { [key: string]: number } = {};
    monthHeaders.forEach((monthKey) => {
      const [month, year] = monthKey.split('-');
      const total = orgCloudCostList.reduce((sum: number, account: Account) => {
        const cost = account.accountCost?.find(
          (c: AccountCost) => c.month_number === month && c.year_number === year
        );
        return sum + (cost ? cost.totalcost : 0);
      }, 0);
      monthTotals[monthKey] = total;
    });

    const currentMonth = monthHeaders[0];
    const previousMonth = monthHeaders[1];
    const currentCost = monthTotals[currentMonth] || 0;
    const previousCost = monthTotals[previousMonth] || 0;
    const growthRate =
      previousCost > 0
        ? ((currentCost - previousCost) / previousCost) * 100
        : 0;

    // Find highest cost account for current month
    let highestAccount = { name: 'N/A', cost: 0 };
    orgCloudCostList.forEach((account: Account) => {
      const [month, year] = currentMonth.split('-');
      const cost = account.accountCost?.find(
        (c: AccountCost) => c.month_number === month && c.year_number === year
      );
      if (cost && cost.totalcost > highestAccount.cost) {
        highestAccount = { name: account.accountName, cost: cost.totalcost };
      }
    });

    const avgCostPerAccount =
      orgCloudCostList.length > 0 ? currentCost / orgCloudCostList.length : 0;

    return {
      currentCost,
      previousCost,
      growthRate,
      avgCostPerAccount,
      highestAccount,
      monthTotals,
    };
  };

  // Prepare line chart data
  const prepareLineChartData = () => {
    const metrics = calculateMetrics();
    if (!metrics) return [];

    return monthHeaders
      .slice()
      .reverse()
      .map((monthKey) => ({
        month: formatMonthHeader(monthKey),
        cost: metrics.monthTotals[monthKey] || 0,
      }));
  };

  const metrics = calculateMetrics();
  const lineChartData = prepareLineChartData();

  // Metric Card Component
  const MetricCard = ({
    title,
    value,
    subtitle,
    icon,
    trend,
  }: {
    title: string;
    value: string;
    subtitle?: string;
    icon: React.ReactNode;
    trend?: { value: number; isPositive: boolean };
  }) => (
    <Card
      style={{
        height: '100%',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        border: '1px solid #f1f5f9',
      }}
    >
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={1.5}
        >
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ fontWeight: 500 }}
          >
            {title}
          </Typography>
          {icon}
        </Box>
        <Typography
          variant="h4"
          style={{ fontWeight: 700, marginBottom: '8px' }}
        >
          {value}
        </Typography>
        {trend && (
          <Box display="flex" alignItems="center">
            {trend.isPositive ? (
              <TrendingUp style={{ color: '#ef4444' }} />
            ) : (
              <TrendingDown style={{ color: '#10b981' }} />
            )}
            <Typography
              variant="body2"
              style={{
                color: trend.isPositive ? '#ef4444' : '#10b981',
                marginRight: '3px',
                fontWeight: 600,
              }}
            >
              {Math.abs(trend.value).toFixed(1)}%
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {' '}
              vs last month
            </Typography>
          </Box>
        )}
        {subtitle && (
          <Typography variant="body2" color="textSecondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <>
      <div className="organizationDetails">
        <div className="OrgDetails-uppterStrip">
          <TitleHeader
            extraContent={
              <div className="d-flex align-center">
                <div style={{ minWidth: '300px', marginRight: '16px' }}>
                  <MultipleSelect
                    options={accountOptions}
                    selectedValues={selectedAccountIds}
                    onChange={setSelectedAccountIds}
                    placeholder="All Accounts"
                    label="Filter Accounts"
                    maxVisibleChips={2}
                    highlightCheckedColor="#1976d2"
                  />
                </div>
                {isStatusLoad ? (
                  <PlanBadgeSkeleton />
                ) : (
                  <PlanBadge
                    planName={orgPlanDetail?.planName}
                    planCost={
                      orgPlanDetail?.planCost
                        ? `$${orgPlanDetail?.planCost}`
                        : '$0.00'
                    }
                    status={orgPlanDetail?.status}
                    planDaysRemaining={orgPlanDetail?.planDaysRemaining}
                  />
                )}
              </div>
            }
          >
            {companyName}
          </TitleHeader>
        </div>

        <div className="OrgDetails-bottomStrip">
          <div className="OrgDetails-leftPanel">
            {/* Metric Cards */}
            <Box mb={2}>
              {isOrgCostListLoad ? (
                <Grid container spacing={2}>
                  {[1, 2, 3, 4].map((i) => (
                    <Grid item xs={12} sm={6} md={6} lg={3} key={i}>
                      <Card
                        style={{
                          height: '126px',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                          border: '1px solid #f1f5f9',
                        }}
                      >
                        <CardContent>
                          <Skeleton variant="text" width="60%" height={20} />
                          <Skeleton
                            variant="text"
                            width="80%"
                            height={40}
                            style={{ marginTop: '12px' }}
                          />
                          <Skeleton variant="text" width="50%" height={16} />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={6} lg={3}>
                    <MetricCard
                      title="Current Month Cost"
                      value={`$${metrics?.currentCost.toFixed(2) || '0.00'}`}
                      icon={<AttachMoney style={{ color: '#3b82f6' }} />}
                      trend={
                        metrics && metrics.growthRate !== 0
                          ? {
                              value: metrics.growthRate,
                              isPositive: metrics.growthRate > 0,
                            }
                          : undefined
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6} lg={3}>
                    <MetricCard
                      title="MoM Growth Rate"
                      value={
                        metrics
                          ? `${
                              metrics.growthRate > 0 ? '+' : ''
                            }${metrics.growthRate.toFixed(1)}%`
                          : '0%'
                      }
                      subtitle={
                        metrics && metrics.growthRate > 0
                          ? 'Increasing trend'
                          : 'Decreasing trend'
                      }
                      icon={
                        metrics && metrics.growthRate > 0 ? (
                          <TrendingUp
                            style={{ fontSize: 20, color: '#ef4444' }}
                          />
                        ) : (
                          <TrendingDown
                            style={{ fontSize: 20, color: '#10b981' }}
                          />
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6} lg={3}>
                    <MetricCard
                      title="Avg Cost/Account"
                      value={`$${
                        metrics?.avgCostPerAccount.toFixed(2) || '0.00'
                      }`}
                      subtitle={`${
                        orgCloudCostList?.length || 0
                      } active accounts`}
                      icon={<People style={{ color: '#8b5cf6' }} />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6} lg={3}>
                    <MetricCard
                      title="Highest Cost Account"
                      value={`$${
                        metrics?.highestAccount.cost.toFixed(2) || '0.00'
                      }`}
                      subtitle={metrics?.highestAccount.name || 'N/A'}
                      icon={<ErrorOutline style={{ color: '#f59e0b' }} />}
                    />
                  </Grid>
                </Grid>
              )}
            </Box>

            {/* Cost Trend Chart */}
            <Box mb={2}>
              <Card style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    style={{ fontWeight: 600, marginBottom: '16px' }}
                  >
                    Cost Trend Analysis
                  </Typography>
                  {isOrgCostListLoad ? (
                    <Skeleton variant="rectangular" width="100%" height={250} />
                  ) : (
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={lineChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis
                          dataKey="month"
                          stroke="#6b7280"
                          style={{ fontSize: '12px' }}
                        />
                        <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            fontSize: '13px',
                          }}
                          formatter={(value: any) => [
                            `$${Number(value).toFixed(2)}`,
                            'Cost',
                          ]}
                        />
                        <Line
                          type="monotone"
                          dataKey="cost"
                          stroke="#3b82f6"
                          strokeWidth={3}
                          dot={{ fill: '#3b82f6', r: 5 }}
                          activeDot={{ r: 7 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Box>

            {/* Table */}
            <div className="OrgDetails-bs-secondPart">
              <TableContainer
                className="accContainer"
                sx={{ maxHeight: '70vh', overflowY: 'auto' }}
              >
                <Table
                  className="table adminTable OrgTable"
                  size="small"
                  stickyHeader
                  aria-label="a dense table"
                >
                  <TableHead className="admintableHead">
                    <TableRow>
                      <TableCell
                        className="adminTableheading adminSrNoSection"
                        align="right"
                      >
                        S.No.
                      </TableCell>
                      <TableCell
                        className="adminTableheading accountName"
                        width={'25%'}
                      >
                        Account Name
                      </TableCell>
                      {monthHeaders.map((monthKey) => (
                        <TableCell
                          key={monthKey}
                          className="adminTableheading"
                          align="right"
                        >
                          {formatMonthHeader(monthKey)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isOrgCostListLoad ? (
                      <TableRow>
                        <TableCell colSpan={monthHeaders.length + 2}>
                          Loading...
                        </TableCell>
                      </TableRow>
                    ) : orgCloudCostList && orgCloudCostList.length ? (
                      <>
                        {orgCloudCostList.map((account: Account, i: number) => (
                          <TableRow
                            key={account.accountName + i}
                            className={i % 2 === 0 ? 'oddRow' : 'evenRow'}
                          >
                            <TableCell align="right">{i + 1}</TableCell>
                            <TableCell>{account.accountName}</TableCell>
                            {monthHeaders.map((monthKey) => (
                              <TableCell key={monthKey} align="right">
                                {getCostForMonth(account.accountCost, monthKey)}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                        {/* Total Cost Row */}
                        <TableRow style={{ backgroundColor: '#f3f4f6' }}>
                          <TableCell
                            colSpan={2}
                            style={{ fontWeight: 700, fontSize: '15px' }}
                          >
                            Total Cost
                          </TableCell>
                          {monthHeaders.map((monthKey) => {
                            const [month, year] = monthKey.split('-');
                            const total = orgCloudCostList.reduce(
                              (sum: number, account: Account) => {
                                const cost = account.accountCost?.find(
                                  (c: AccountCost) =>
                                    c.month_number === month &&
                                    c.year_number === year
                                );
                                return sum + (cost ? cost.totalcost : 0);
                              },
                              0
                            );
                            return (
                              <TableCell
                                key={monthKey}
                                align="right"
                                style={{ fontWeight: 700, fontSize: '15px' }}
                              >
                                ${total.toFixed(2)}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      </>
                    ) : (
                      <TableRow>
                        <TableCell colSpan={monthHeaders.length + 2}>
                          No Records Found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </div>

          <div className="OrgDetails-rightPanel">
            <TopServicesWidget
              services={topServiceWiseCost}
              loading={topServiceWiseLoad}
              companyName={companyName}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default OrganizationDetails;
